import React, { useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { Landmark, Loader2, MapPin, Check } from 'lucide-react';
import { Input } from './ui/input';
import { Label } from './ui/label';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Accent-insensitive normaliser (Rivoli ↔ Rívoli, cafe ↔ café).
const stripAccents = (s) =>
  (s || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase();

// Word-level tokens after stripping accents. Used to rank scoped matches so
// "Enjoy Antofagasta" typed also lights up a venue called "Enjoy" or "Casino
// Enjoy Antofagasta" — the operator picked the commune already, so being
// generous with tokens is safe.
const tokens = (s) =>
  stripAccents(s)
    .replace(/[^a-z0-9\s]/g, ' ')
    .split(/\s+/)
    .filter((t) => t.length >= 2);

/**
 * VenueAutocomplete
 * Two modes:
 * - Scoped (commune_id / region_id set): preloads the full list of venues in
 *   that scope on mount; the dropdown shows every venue immediately so the
 *   user can pick without typing. When the operator types a name that
 *   overlaps a single venue in that scope, the venue is auto-picked so the
 *   parent form's address / lat / lng populate without a manual click.
 * - Global (no scope): as the user types (>= 2 chars) queries the backend
 *   accent-insensitively; each suggestion shows the venue's commune + region
 *   so the operator can verify where it is at a glance. Picking a venue
 *   auto-fills commune/region on the parent form.
 */
export function VenueAutocomplete({
  label = 'Nombre del recinto',
  placeholder = 'Empieza a escribir un lugar…',
  initialText = '',
  communeId,
  regionId,
  onSelect,
  onTextChange,
  testId = 'venue-autocomplete',
}) {
  const [text, setText] = useState(initialText || '');
  const [items, setItems] = useState([]);   // list for the current mode
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [autoMatched, setAutoMatched] = useState(null); // venue auto-picked in scoped mode
  const containerRef = useRef(null);
  const debounceRef = useRef(null);
  const lastAutoPickedIdRef = useRef(null); // avoids re-picking the same venue every keystroke
  const hasScope = !!(communeId || regionId);

  // Sync when parent supplies a new initialText (e.g. edit mode load).
  useEffect(() => { setText(initialText || ''); }, [initialText]);

  useEffect(() => {
    const onClick = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, []);

  // SCOPED mode: preload every venue in the picked region/commune.
  useEffect(() => {
    if (!hasScope) { setItems([]); return; }
    lastAutoPickedIdRef.current = null;
    setAutoMatched(null);
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      try {
        const params = new URLSearchParams({ limit: '200' });
        if (communeId) params.set('commune_id', communeId);
        if (regionId) params.set('region_id', regionId);
        const res = await axios.get(`${API_URL}/api/venues/search?${params.toString()}`);
        if (!cancelled) setItems(Array.isArray(res.data) ? res.data : []);
      } catch {
        if (!cancelled) setItems([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [communeId, regionId, hasScope]);

  // GLOBAL mode: debounced backend query as user types.
  const runGlobalSearch = async (term) => {
    if (term.trim().length < 2) { setItems([]); return; }
    setLoading(true);
    try {
      const params = new URLSearchParams({ q: term.trim(), limit: '15' });
      const res = await axios.get(`${API_URL}/api/venues/search?${params.toString()}`);
      setItems(Array.isArray(res.data) ? res.data : []);
    } catch {
      setItems([]);
    } finally {
      setLoading(false);
    }
  };

  // Local filter is only used in scoped mode (we already have the whole list).
  // In scoped mode we're already inside the picked region/commune so we can be
  // very lenient — accept substring match in either direction plus token
  // overlap. This is what lets typing "Enjoy Antofagasta" surface a venue
  // stored as just "Enjoy" (or vice versa) without extra clicks.
  const filtered = useMemo(() => {
    if (!hasScope) return items;
    const needle = stripAccents(text.trim());
    if (!needle) return items;
    const needleTokens = tokens(text);
    const scored = items
      .map((v) => {
        const hay = stripAccents(v.name);
        if (!hay) return null;
        // Direct substring match either way — the strongest signal.
        if (hay.includes(needle) || needle.includes(hay)) {
          return { v, score: 3 + Math.min(hay.length, needle.length) / 100 };
        }
        // Token overlap: at least one 2+ char word shared.
        const hayTokens = tokens(v.name);
        const shared = needleTokens.filter((t) => hayTokens.includes(t));
        if (shared.length > 0) return { v, score: 1 + shared.length };
        return null;
      })
      .filter(Boolean)
      .sort((a, b) => b.score - a.score);
    return scored.map((s) => s.v);
  }, [items, text, hasScope]);

  // Auto-pick: when the user has typed a venue name whose match is
  // "unambiguous enough" in the current scope, silently fill lat/lng/address
  // on the parent form. Triggered synchronously with `filtered`. Re-picks
  // require the id to have changed so editing the text after an auto-match
  // doesn't loop or spam toasts.
  useEffect(() => {
    if (!hasScope) { setAutoMatched(null); lastAutoPickedIdRef.current = null; return; }
    const clean = text.trim();
    if (clean.length < 3) { setAutoMatched(null); return; }
    const top = filtered[0];
    if (!top) { setAutoMatched(null); return; }
    // Recompute the top candidate's score (mirroring the filter logic) so we
    // can decide unambiguity without re-scoring the whole list.
    const hay = stripAccents(top.name);
    const needle = stripAccents(clean);
    const topSubstring = hay.includes(needle) || needle.includes(hay);
    // Auto-pick when either:
    // • the scope only has this one venue that matches at all, or
    // • the top candidate is a full substring match AND the runner-up is
    //   demonstrably weaker (only token-overlap, no substring). This keeps
    //   the auto-pick silent when two "Enjoy" venues would compete.
    let unambiguous = filtered.length === 1;
    if (!unambiguous && topSubstring && filtered[1]) {
      const runnerHay = stripAccents(filtered[1].name);
      const runnerSubstring = runnerHay.includes(needle) || needle.includes(runnerHay);
      unambiguous = !runnerSubstring;
    } else if (!unambiguous && topSubstring) {
      unambiguous = true;
    }
    if (!unambiguous) { setAutoMatched(null); return; }
    if (lastAutoPickedIdRef.current === top.id) return;
    lastAutoPickedIdRef.current = top.id;
    setAutoMatched(top);
    onSelect && onSelect(top);
    // Note: parent's onSelect will normalise the input's text to the venue's
    // canonical name, which is desirable — typos like "Enjoi" become "Enjoy".
  }, [filtered, text, hasScope]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleChange = (e) => {
    const v = e.target.value;
    setText(v);
    onTextChange && onTextChange(v);
    setOpen(true);
    if (!hasScope) {
      if (debounceRef.current) clearTimeout(debounceRef.current);
      debounceRef.current = setTimeout(() => runGlobalSearch(v), 250);
    }
  };

  const pick = (venue) => {
    setText(venue.name);
    onTextChange && onTextChange(venue.name);
    onSelect && onSelect(venue);
    setOpen(false);
  };

  return (
    <div ref={containerRef} className="relative">
      {label && <Label className="flex items-center gap-1"><Landmark className="w-3.5 h-3.5" /> {label}</Label>}
      <div className="relative">
        <Input
          value={text}
          onChange={handleChange}
          onFocus={() => setOpen(true)}
          placeholder={placeholder}
          data-testid={testId}
        />
        {loading && (
          <Loader2 className="w-4 h-4 animate-spin absolute right-3 top-1/2 -translate-y-1/2 text-gray-400" />
        )}
      </div>
      {open && filtered.length > 0 && (
        <div
          className="absolute left-0 right-0 z-50 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-64 overflow-y-auto"
          data-testid={`${testId}-suggestions`}
        >
          {hasScope && (
            <div className="text-[10px] font-semibold uppercase tracking-wider text-gray-400 px-3 pt-2 pb-1 border-b bg-gray-50">
              {filtered.length} recinto{filtered.length !== 1 ? 's' : ''} en esta zona
            </div>
          )}
          {filtered.map((v) => (
            <button
              key={v.id}
              type="button"
              onClick={() => pick(v)}
              className="w-full text-left px-3 py-2 hover:bg-emerald-50 flex items-start gap-2 border-b last:border-b-0"
              data-testid={`${testId}-item-${v.id}`}
            >
              <MapPin className="w-3.5 h-3.5 mt-1 text-emerald-600 shrink-0" />
              <div className="min-w-0 flex-1">
                <div className="text-sm font-semibold text-[#064E3B] truncate">{v.name}</div>
                <div className="text-[11px] text-gray-500 truncate">
                  {v.commune_name && (
                    <span className="inline-flex items-center gap-1 mr-2 font-medium text-emerald-700">
                      <MapPin className="w-3 h-3" />{v.commune_name}
                      {v.region_name && v.region_name !== v.commune_name ? `, ${v.region_name}` : ''}
                    </span>
                  )}
                  {v.address && <span className="text-gray-400">· {v.address}</span>}
                </div>
              </div>
            </button>
          ))}
        </div>
      )}
      {open && !loading && filtered.length === 0 && text.trim().length >= (hasScope ? 1 : 2) && (
        <div className="absolute left-0 right-0 z-50 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg px-3 py-2 text-xs text-gray-500">
          Sin coincidencias — se guardará como texto libre.
        </div>
      )}
      {open && !loading && !hasScope && text.trim().length < 2 && (
        <div className="absolute left-0 right-0 z-50 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg px-3 py-2 text-[11px] text-gray-500">
          Escribe al menos 2 letras para buscar entre todos los recintos guardados.
        </div>
      )}
      {open && !loading && hasScope && items.length === 0 && (
        <div className="absolute left-0 right-0 z-50 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg px-3 py-2 text-xs text-gray-500">
          No hay recintos guardados en esta zona todavía.
        </div>
      )}
      {autoMatched && !open && (
        <div
          className="mt-1 flex items-center gap-1.5 text-[11px] text-emerald-700"
          data-testid={`${testId}-automatch`}
          title={`Se rellenó automáticamente dirección y coordenadas de "${autoMatched.name}"`}
        >
          <Check className="w-3 h-3" />
          <span>
            Coincidencia detectada: <span className="font-semibold">{autoMatched.name}</span>
            {autoMatched.address ? <span className="text-emerald-600/80"> · {autoMatched.address}</span> : null}
          </span>
        </div>
      )}
    </div>
  );
}
